/**
 * TaskBasicFields Component
 *
 * Task Details section:
 * - Task Name
 * - Request Type / Request Details (sub-type)
 * - Start Date / Due Date
 * - Channel Partner / Advertiser (revealed once a request type is chosen)
 */

import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Card, Col, Row, Form, Input, Select, Cascader, DatePicker, Tag } from 'antd';
import { CalendarOutlined } from '@ant-design/icons';
import Text from 'antd/lib/typography/Text';
import moment from 'moment';
import { taskTypes, subTypes } from '../../configs/task.config';
import '../../styles/index.scss';

const { Option } = Select;

// Request Type -> Sub-Type as one cascading field; types with no sub-types are leaves.
const requestTypeOptions = taskTypes.map((type) => {
  const subs = subTypes[type] || [];
  return {
    value: type,
    label: type,
    children: subs.length ? subs.map((s) => ({ value: s, label: s })) : undefined,
  };
});

// A partner literally named "MediaJel Direct" is the direct umbrella — every advertiser picked
// under it is a direct deal (compared case-insensitively).
const DIRECT_PARTNER_NAME = 'mediajel direct';

const TaskBasicFields = ({
  form,
  level,
  windowWidth,
  userOrgName,
  channelPartnerOrgs,
  advertiserOrgs,
  basicOrgsDetailsLoading,
  fetchBasicOrgs,
  selectedProjectId,
  handleSubTypeChange,
  setTaskDatePickerVisible,
  setTaskStartDatePickerVisible,
}) => {
  // Direct detection — flip the deal to DIRECT (Direct tag + single budget/CPM) when any of:
  //  - the partner's level is CHANNEL_PARTNER_DIRECT (a direct-level org); or
  //  - the partner is "MediaJel Direct": every advertiser picked under it is a direct; or
  //  - the SELECTED advertiser shares the partner's name — the partner IS that advertiser.
  // The last rule reads the advertiser that was actually picked (it used to read "the only
  // advertiser in the loaded list", so a deal flipped whenever the dropdown re-fetched and the
  // list grew or shrank) and mirrors isDirectTask, the rule the read surfaces tag with.
  const channelPartnerId = Form.useWatch('channelPartnerId', form);
  const advertiserId = Form.useWatch('advertiserId', form);
  const selectedCp = (channelPartnerOrgs || []).find((o) => o.id === channelPartnerId);
  const norm = (s) => (s || '').trim().toLowerCase();
  // For non-PLATFORM users the Channel Partner is locked to their own org (the field is disabled
  // and only shows the org name) — there's no selectedCp object to read, so derive the partner's
  // name and level from the current user's org. Otherwise "Direct" could never be detected for
  // them, even when their single advertiser shares the org name.
  const isNonPlatform = level !== 'PLATFORM';
  const partnerName = isNonPlatform ? userOrgName : selectedCp?.name;
  const partnerIsDirectLevel = isNonPlatform
    ? level === 'CHANNEL_PARTNER_DIRECT'
    : selectedCp?.level === 'CHANNEL_PARTNER_DIRECT';
  const hasPartner = isNonPlatform ? !!partnerName : !!selectedCp;
  // Searching the Advertiser select re-fetches `advertiserOrgs` and can drop the picked org from
  // the list, so remember its name from the pick itself and fall back to that.
  const [pickedAdvertiserName, setPickedAdvertiserName] = useState(null);
  const advertiserName =
    (advertiserOrgs || []).find((o) => o.id === advertiserId)?.name || pickedAdvertiserName;
  const isDirectWith = (advName) =>
    hasPartner &&
    (partnerIsDirectLevel ||
      norm(partnerName) === DIRECT_PARTNER_NAME ||
      (!!advName && norm(advName) === norm(partnerName)));
  const isDirect = isDirectWith(advertiserName);
  // Only re-derive dealType after the user actively changes the partner. On EDIT the saved
  // dealType is restored by the registry populate; the advertiser list isn't fully loaded yet,
  // so an unguarded derivation could wrongly flip the deal and null out the saved CPM/budget.
  const partnerSelectionTouched = useRef(false);
  useEffect(() => {
    if (!partnerSelectionTouched.current) return;
    form.setFieldsValue({
      dealType: hasPartner ? (isDirect ? 'DIRECT' : 'CHANNEL_PARTNER') : undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirect, channelPartnerId]);

  return (
    <Col className="task-basic-fields">
      <Card
        className="task-basic-fields__card"
        title={<Text className="task-basic-fields__card-title">TASK DETAILS</Text>}
      >
        {/* Row 1: Task Name */}
        <Row gutter={windowWidth < 768 ? 16 : 32}>
          <Col xs={24} sm={24} md={12} lg={12}>
            <Form.Item
              name="name"
              label={
                <div>
                  <div>Task Name</div>
                  <div className="task-basic-fields__label-hint">
                    The name you will identify this task by
                  </div>
                </div>
              }
              rules={[{ required: true, message: 'Task Name is Required' }]}
              colon={false}
            >
              <Input placeholder="Enter task name" className="custom-input" />
            </Form.Item>
          </Col>
          {level === 'PLATFORM' && (
            <Col xs={24} sm={12} md={6} lg={6}>
              <Form.Item
                name="startDate"
                label={
                  <div>
                    <div>Start Date</div>
                    <div className="task-basic-fields__label-hint">Optional</div>
                  </div>
                }
                colon={false}
                layout="vertical"
                className="task-w--full"
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const dueDate = getFieldValue('dueDate');
                      if (!value || !dueDate) {
                        return Promise.resolve();
                      }
                      if (moment(value).isAfter(moment(dueDate), 'day')) {
                        return Promise.reject(new Error('Start date cannot be after due date'));
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <DatePicker
                  onOpenChange={(open) => {
                    if (open) {
                      setTaskStartDatePickerVisible(true);
                    }
                  }}
                  className="custom-date task-w--full"
                  open={false}
                  suffixIcon={<CalendarOutlined />}
                />
              </Form.Item>
            </Col>
          )}
          <Col
            xs={24}
            sm={12}
            md={level === 'PLATFORM' ? 6 : 12}
            lg={level === 'PLATFORM' ? 6 : 12}
          >
            <Form.Item
              name="dueDate"
              label={
                <div>
                  <div>Due Date</div>
                  <div className="task-basic-fields__label-hint">
                    {level === 'PLATFORM' && selectedProjectId ? 'Optional' : 'Completion'}
                  </div>
                </div>
              }
              colon={false}
              layout="vertical"
              className="task-w--full"
              rules={[
                {
                  required: !selectedProjectId,
                  message: 'Due Date is Required',
                },
                ...(level === 'PLATFORM'
                  ? [
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          const startDate = getFieldValue('startDate');
                          if (!startDate || !value) {
                            return Promise.resolve();
                          }
                          if (moment(value).isBefore(moment(startDate), 'day')) {
                            return Promise.reject(new Error('Due date cannot be before start date'));
                          }
                          return Promise.resolve();
                        },
                      }),
                    ]
                  : []),
              ]}
            >
              <DatePicker
                onOpenChange={(open) => {
                  if (open) {
                    setTaskDatePickerVisible(true);
                  }
                }}
                className="custom-date task-w--full"
                open={false}
                suffixIcon={<CalendarOutlined />}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Row 2: Request Type — cascades to a sub-type when the type has one */}
        <Row gutter={32}>
          <Form.Item name="requestType" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="requestSubType" hidden>
            <Input />
          </Form.Item>
          <Col xs={24} sm={24} md={12} lg={12}>
            <Form.Item
              name="requestTypePath"
              label={
                <div>
                  <div>Request Type</div>
                  <div className="task-basic-fields__label-hint">
                    Select the type — and a sub-type if it has one
                  </div>
                </div>
              }
              rules={[{ required: true, message: 'Request Type is Required' }]}
              colon={false}
            >
              <Cascader
                options={requestTypeOptions}
                onChange={(value) => handleSubTypeChange(value || [])}
                changeOnSelect
                allowClear
                placeholder="Select request type"
                showSearch={{
                  filter: (input, path) =>
                    path.some((opt) =>
                      (opt.label || '').toLowerCase().includes(input.toLowerCase())
                    ),
                }}
                className="custom-select task-w--full"
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Row 4: Channel Partner + Advertiser */}
        <Row gutter={32}>
            <Form.Item name="dealType" hidden>
              <Input />
            </Form.Item>
            <Col xs={24} sm={24} md={12} lg={12}>
              <Form.Item
                label={
                  <div>
                    <div>Channel Partner</div>
                    <div className="task-basic-fields__label-hint">
                      The partner this task is regarding
                    </div>
                  </div>
                }
                rules={
                  level !== 'PLATFORM'
                    ? []
                    : [{ required: true, message: 'Channel Partner is Required' }]
                }
                colon={false}
                name="channelPartnerId"
              >
                <Select
                  allowClear
                  placeholder={level !== 'PLATFORM' ? userOrgName : 'Select Partner'}
                  showSearch
                  disabled={level !== 'PLATFORM'}
                  loading={basicOrgsDetailsLoading}
                  onFocus={() => fetchBasicOrgs('', 'CHANNEL_PARTNER')}
                  onSearch={(searchText) => fetchBasicOrgs(searchText, 'CHANNEL_PARTNER')}
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                  filterSort={(optionA, optionB) =>
                    optionA.children.toLowerCase().localeCompare(optionB.children.toLowerCase())
                  }
                  onChange={() => {
                    partnerSelectionTouched.current = true;
                    setPickedAdvertiserName(null);
                    form.setFieldsValue({ advertiserId: [] });
                    fetchBasicOrgs('', 'ADVERTISER');
                  }}
                  className="custom-select"
                >
                  {channelPartnerOrgs.map((org) => (
                    <Option key={org.id} value={org.id}>
                      {org.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={12} lg={12}>
              <Form.Item
                name="advertiserId"
                colon={false}
                label={
                  <div>
                    <div>
                      Advertiser{' '}
                      {isDirect && (
                        <Tag color="green" className="task-basic-fields__direct-tag">
                          Direct
                        </Tag>
                      )}
                    </div>
                    <div className="task-basic-fields__label-hint">
                      The advertiser this task is regarding
                    </div>
                  </div>
                }
                rules={[{ required: true, message: 'Advertiser is Required' }]}
              >
                <Select
                  placeholder="Select Advertiser"
                  showSearch
                  loading={basicOrgsDetailsLoading}
                  onFocus={() => fetchBasicOrgs('', 'ADVERTISER')}
                  onSearch={(searchText) => fetchBasicOrgs(searchText, 'ADVERTISER')}
                  onChange={(value, option) => {
                    // Picking the advertiser is the deal-defining action for non-PLATFORM users
                    // (their Channel Partner is locked, so its onChange never fires). Derive from
                    // the org just picked — the watched advertiserId only updates next render.
                    partnerSelectionTouched.current = true;
                    const advName = option?.children || null;
                    setPickedAdvertiserName(advName);
                    form.setFieldsValue({
                      dealType: isDirectWith(advName) ? 'DIRECT' : 'CHANNEL_PARTNER',
                    });
                  }}
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                  filterSort={(optionA, optionB) =>
                    optionA.children.toLowerCase().localeCompare(optionB.children.toLowerCase())
                  }
                  className="custom-select"
                >
                  {advertiserOrgs.map((org) => (
                    <Option key={org.id} value={org.id}>
                      {org.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
        </Row>
      </Card>
    </Col>
  );
};

TaskBasicFields.propTypes = {
  form: PropTypes.object.isRequired,
  level: PropTypes.string,
  windowWidth: PropTypes.number.isRequired,
  userOrgName: PropTypes.string,
  channelPartnerOrgs: PropTypes.array,
  advertiserOrgs: PropTypes.array,
  basicOrgsDetailsLoading: PropTypes.bool,
  fetchBasicOrgs: PropTypes.func,
  selectedProjectId: PropTypes.string,
  handleSubTypeChange: PropTypes.func.isRequired,
  setTaskDatePickerVisible: PropTypes.func.isRequired,
  setTaskStartDatePickerVisible: PropTypes.func.isRequired,
};

TaskBasicFields.defaultProps = {
  level: null,
  userOrgName: '',
  channelPartnerOrgs: [],
  advertiserOrgs: [],
  basicOrgsDetailsLoading: false,
  fetchBasicOrgs: () => {},
  selectedProjectId: null,
};

export default TaskBasicFields;
